import Anthropic from '@anthropic-ai/sdk';

const getAnthropicClient = () => {
  const apiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY?.trim();
  if (!apiKey) return null;
  return new Anthropic({
    apiKey,
    dangerouslyAllowBrowser: true,
  });
};

const anthropic = getAnthropicClient();

/**
 * Get chat completion from Anthropic API
 * @param {Array} messages - Array of message objects with role and content
 * @param {boolean} stream - Whether to stream the response
 * @param {Object} options - Additional options (model, maxTokens, temperature, onChunk)
 * @returns {Promise<Object>} SDK Response or handled stream
 */
export const getChatCompletion = async (messages, stream = false, options = {}) => {
  try {
    const rawApiKey = import.meta.env?.VITE_ANTHROPIC_API_KEY;
    const apiKey = rawApiKey?.trim();

    if (!apiKey || apiKey === 'undefined' || apiKey === 'null') {
      console.error('❌ Missing or invalid VITE_ANTHROPIC_API_KEY');
      throw new Error('Anthropic API key is missing or invalid. Please check your environment variables.');
    }

    // Ensure we have a valid client
    if (!anthropic) {
      console.error('❌ Anthropic client not initialized');
      throw new Error('Anthropic client failed to initialize. Check API key presence.');
    }

    // Filter out system messages and extract content for the system prompt
    const systemContent = messages
      ?.filter(m => m?.role === 'system')
      ?.map(m => m?.content)
      ?.join('\n') || '';

    const conversationMessages = messages
      ?.filter(m => m?.role !== 'system')
      ?.map(m => ({
        role: m?.role === 'assistant' ? 'assistant' : 'user',
        content: m?.content || ''
      }));

    if (conversationMessages.length === 0) {
      throw new Error('Anthropic requires at least one user message to begin a conversation.');
    }

    const model = options?.model || 'claude-3-5-sonnet-20240620';
    const params = {
      model,
      max_tokens: options?.maxTokens || 4096,
      temperature: options?.temperature ?? 0.7,
      messages: conversationMessages,
      system: systemContent || undefined,
    };

    console.log(`🚀 [Anthropic SDK] Requesting ${model} (Stream: ${stream}, Messages: ${conversationMessages.length})`);

    try {
      if (!stream) {
        return await anthropic.messages.create(params);
      }
      return anthropic.messages.stream(params);
    } catch (apiError) {
      // Handle Model Not Found (404) with Fallback
      if (apiError.status === 404 && model !== 'claude-3-5-sonnet-20240620') {
        console.warn(`⚠️ Model ${model} not found. Falling back to claude-3-5-sonnet-20240620...`);
        const fallbackParams = { ...params, model: 'claude-3-5-sonnet-20240620' };
        if (!stream) {
          return await anthropic.messages.create(fallbackParams);
        }
        return anthropic.messages.stream(fallbackParams);
      }

      // Enhance error with request_id for debugging
      const requestId = apiError.request_id || 'unknown';
      const errorMessage = `${apiError.status} ${apiError.message} (Request ID: ${requestId})`;
      console.error(`❌ [Anthropic SDK] API Error:`, errorMessage);
      throw new Error(errorMessage);
    }

  } catch (error) {
    console.error('❌ [Anthropic SDK] Request failed:', error.message);
    throw error;
  }
};

export const checkSpam = async (content) => {
  try {
    const messages = [
      {
        role: 'user',
        content: `You are a B2B content moderation expert. Analyze the following business inquiry and provide THREE scores on a 0-100 scale:

**1. Spam Score (0-100)**: How likely is this spam?
- 0-20: Legitimate business inquiry
- 21-50: Borderline/Needs review
- 51-100: Likely spam

**2. Buying Intent Score (0-100)**: How strong is the buyer's purchase intent?
- 0-20: Just browsing/no interest
- 21-50: Exploring options
- 51-80: Strong interest, close to decision
- 81-100: Ready to buy now, high urgency

**3. B2B Relevance (0-100)**: How relevant is this to B2B business topics?
- 0-30: Off-topic/irrelevant
- 31-60: Somewhat related
- 61-100: Highly relevant B2B topic

**Content to analyze:**
"${content}"

**Required Response Format (JSON ONLY):**
{
  "spamScore": [0-100],
  "buyingIntent": [0-100],
  "b2bRelevance": [0-100],
  "reasoning": "[Brief explanation of your scores]"
}

**Professional Guidelines:**
1. Return ONLY valid JSON.
2. Moderate with a focus on B2B professional standards.
3. Identify "High Value Leads" who express clear problems our services can solve.
4. Flag "Low Value/Spam" content that is generic, promotional, or off-topic.
5. Consider context: business inquiries about marketing, SEO, and tech are always high-value.`,
      },
    ];

    const message = await getChatCompletion(messages, false, {
      maxTokens: 1024,
    });

    // Parse response
    const rawText = message?.content?.[0]?.text || '{}';
    console.log('Raw Claude Response:', rawText);

    // Try to extract JSON from the response
    let result;
    try {
      result = JSON.parse(rawText);
    } catch (e) {
      const jsonMatch = rawText?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        result = JSON.parse(jsonMatch?.[0]);
      } else {
        console.error('Failed to parse Claude response:', rawText);
        result = {
          spamScore: 20,
          buyingIntent: 50,
          b2bRelevance: 50,
          reasoning: 'Unable to parse AI response, using default scores'
        };
      }
    }

    return {
      spamScore: result?.spamScore || 20,
      buyingIntent: result?.buyingIntent || 50,
      b2bRelevance: result?.b2bRelevance || 50,
      reasoning: result?.reasoning || 'No reasoning provided',
    };
  } catch (error) {
    console.error('Error checking spam with AI:', error);
    return {
      spamScore: 20,
      buyingIntent: 50,
      b2bRelevance: 50,
      reasoning: 'AI analysis failed, using default values',
    };
  }
};

export const answerQuestion = async (question, context = '') => {
  try {
    const messages = [
      {
        role: 'user',
        content: `You are the Lead Digital Transformation Consultant at b2b.codes. Your goal is to provide expert advice on marketing, sales, SEO, and business operations.

Context: ${context || 'No additional context provided.'}

Question: ${question}

**Your Task:**
Provide a comprehensive, actionable answer that:
1. Directly addresses the question
2. Provides specific, practical advice
3. Uses industry best practices
4. Includes examples when relevant
5. Maintains a professional but approachable tone

**Format:**
- Start with a brief direct answer
- Follow with detailed explanation
- Include actionable steps if applicable
- End with any important considerations or caveats`,
      },
    ];

    const message = await getChatCompletion(messages, false, {
      maxTokens: 4096,
    });

    return message?.content?.[0]?.text || 'Unable to generate answer at this time.';
  } catch (error) {
    console.error('Error generating answer with AI:', error);
    throw error;
  }
};

export const generateArticleSuggestions = async (topic) => {
  try {
    const messages = [
      {
        role: 'user',
        content: `You are a B2B content strategist. Generate 5 article ideas for the topic: "${topic}".

**Required Response Format (JSON ONLY):**
[
  {
    "title": "Article title",
    "description": "Brief description of what the article covers",
    "difficulty": "Beginner|Intermediate|Advanced",
    "estimatedReadTime": "X min read"
  }
]

**Requirements:**
1. Return ONLY valid JSON array, no markdown, no explanations
2. Each article should target B2B professionals
3. Titles should be actionable and specific
4. Mix different difficulty levels
5. Focus on practical, implementable advice`,
      },
    ];

    const message = await getChatCompletion(messages, false, {
      maxTokens: 2048,
    });

    const rawText = message?.content?.[0]?.text || '[]';
    console.log('Raw Claude Article Suggestions:', rawText);

    try {
      const jsonMatch = rawText?.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch?.[0]);
      }
      return JSON.parse(rawText);
    } catch (e) {
      console.error('Failed to parse article suggestions:', rawText);
      return [];
    }
  } catch (error) {
    console.error('Error generating article suggestions:', error);
    throw error;
  }
};

export default {
  getChatCompletion,
  checkSpam,
  answerQuestion,
  generateArticleSuggestions,
};